import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';
import { toast } from 'react-toastify';

const useInvoice = () => {
  const authCtx = useContext(AuthContext);
  const { getAuthHeaders } = authCtx;
  const { isLoading, error, sendRequest } = useHttp();

  // Helper: Validate list response shape; throw to propagate errors like in EditableList
  const assertListResponseOrThrow = data => {
    const isValid = data && Array.isArray(data.items) && typeof data.total === 'number';
    if (!isValid) {
      const msg = data?.message || 'Ungültige Antwort vom Server.';
      throw new Error(msg);
    }
  };

  // Helper: Ensure object-like invoice response
  const assertInvoiceResponseOrThrow = data => {
    const isValid = data && typeof data === 'object' && data._id;
    if (!isValid) {
      const msg = data?.message || 'Aktion fehlgeschlagen.';
      throw new Error(msg);
    }
  };

  // Helper: Ensure reissue response shape
  const assertReissueResponseOrThrow = data => {
    const isValid =
      data &&
      typeof data === 'object' &&
      data.cancellationInvoice &&
      data.cancellationInvoice._id &&
      data.newInvoice &&
      data.newInvoice._id;
    if (!isValid) {
      const msg = data?.message || 'Neu-Ausstellung fehlgeschlagen.';
      throw new Error(msg);
    }
  };

  const getBookingInvoices = useCallback(async bookingId => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/invoices`,
        method: 'GET',
        headers: getAuthHeaders(),
      });
      return responseData;
    } catch (err) {
      console.error('error in useInvoice getBookingInvoices:', err);
      return [];
    }
  }, [sendRequest, getAuthHeaders]);

  const getInvoicePDF = useCallback(async (bookingId, invoiceId) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/internalBooking/${bookingId}/invoice/${invoiceId}/pdf`,
        method: 'GET',
        headers: getAuthHeaders(),
        returnBlob: true,
      });
      return responseData;
    } catch (err) {
      console.error('error in useInvoice getInvoicePDF:', err);
      return null;
    }
  }, [sendRequest, getAuthHeaders]);

  const listInvoices = useCallback(
    async (
      { branch, status, paymentStatus, origin, dunningStatus, krefoStatus, from, to, q, group, page, limit } = {}
    ) => {
      try {
        const params = new URLSearchParams();
        if (branch) params.append('branch', branch);
        if (status) params.append('status', status);
        if (paymentStatus) params.append('paymentStatus', paymentStatus);
        if (origin) params.append('origin', origin);
        if (dunningStatus) params.append('dunningStatus', dunningStatus);
        if (krefoStatus) params.append('krefoStatus', krefoStatus);
        if (from) params.append('from', from);
        if (to) params.append('to', to);
        if (q) params.append('q', q);
        if (group !== undefined) params.append('group', group ? 'true' : 'false');
        if (page) params.append('page', String(page));
        if (limit) params.append('limit', String(limit));

        const responseData = await sendRequest({
          url: `/api/v1/invoice/list/all?${params.toString()}`,
          method: 'GET',
          headers: getAuthHeaders(),
        });
        assertListResponseOrThrow(responseData);
        return responseData;
      } catch (err) {
        toast.error(`Fehler beim Laden der Rechnungen: ${err.message}`);
        throw err;
      }
    },
    [sendRequest, getAuthHeaders]
  );

  const markInvoicePaid = useCallback(async invoiceId => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/mark-paid`,
        method: 'POST',
        headers: getAuthHeaders(),
      });
      assertInvoiceResponseOrThrow(responseData);
      return responseData;
    } catch (err) {
      console.error('error in useInvoice markInvoicePaid:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  // Confirm a payment received outside Adyen (cash/bank transfer) for a no-show
  // invoice. Optionally grants the goodwill ("Kulanz") voucher.
  const confirmNoShowPayment = useCallback(async (invoiceId, grantKulanzVoucher = false) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/confirm-noshow-payment`,
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: { grantKulanzVoucher: !!grantKulanzVoucher },
      });
      if (!responseData || !responseData.invoice) {
        throw new Error(responseData?.message || 'Zahlungsbestätigung fehlgeschlagen');
      }
      return responseData;
    } catch (err) {
      console.error('error in useInvoice confirmNoShowPayment:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  // Cancel (waive) a collectable receivable process (no-show or unpaid-booking
  // invoice) and create a cancellation/storno invoice.
  const cancelNoShowProcess = useCallback(async (invoiceId, reason = '') => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/cancel-noshow`,
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: { reason },
      });
      if (!responseData || responseData.success !== true) {
        throw new Error(responseData?.message || 'Abbruch des Vorgangs fehlgeschlagen');
      }
      return responseData;
    } catch (err) {
      console.error('error in useInvoice cancelNoShowProcess:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  // Manually update the no-show collection statuses: dunning level ("Mahnstufe")
  // and Creditreform ("krefo") handover state. Pass only the fields to change.
  const updateCollectionStatus = useCallback(async (invoiceId, { dunningStatus, krefoStatus } = {}) => {
    try {
      const body = {};
      if (dunningStatus !== undefined) body.dunningStatus = dunningStatus;
      if (krefoStatus !== undefined) body.krefoStatus = krefoStatus;
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/collection-status`,
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body,
      });
      assertInvoiceResponseOrThrow(responseData);
      return responseData;
    } catch (err) {
      console.error('error in useInvoice updateCollectionStatus:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  // Download the Creditreform ("krefo") ZIP export of all overdue, not-yet-handed-over
  // no-show invoices (grouped by location). Returns the ZIP blob. The backend marks the
  // included invoices as handed over to Creditreform.
  const downloadKrefoZip = useCallback(async (branch) => {
    try {
      const params = new URLSearchParams();
      if (branch) params.append('branch', branch);
      const query = params.toString();
      const responseData = await sendRequest({
        url: `/api/v1/invoice/export/krefo-zip${query ? `?${query}` : ''}`,
        method: 'GET',
        headers: getAuthHeaders(),
        returnBlob: true,
      });
      return responseData;
    } catch (err) {
      console.error('error in useInvoice downloadKrefoZip:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  const updateInvoiceCustomer = useCallback(async (invoiceId, payload) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/customer`,
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: payload,
      });
      assertInvoiceResponseOrThrow(responseData);
      return responseData;
    } catch (err) {
      console.error('error in useInvoice updateInvoiceCustomer:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  const getInvoicePDFById = useCallback(async invoiceId => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/pdf`,
        method: 'GET',
        headers: getAuthHeaders(),
        returnBlob: true,
      });
      // Validate PDF signature to avoid treating error blobs as success
      if (responseData && typeof responseData.slice === 'function') {
        const header = await responseData.slice(0, 4).text();
        if (header !== '%PDF') {
          throw new Error('Ungültige PDF-Antwort');
        }
      }
      return responseData;
    } catch (err) {
      console.error('error in useInvoice getInvoicePDFById:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  const createCancellation = useCallback(async (invoiceId, reductions) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/cancel-lines`,
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: { reductions },
      });
      assertInvoiceResponseOrThrow(responseData);
      return responseData;
    } catch (err) {
      console.error('error in useInvoice createCancellation:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  const reissueWithCancellation = useCallback(async (invoiceId, payload) => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/reissue`,
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: payload,
      });
      assertReissueResponseOrThrow(responseData);
      return responseData;
    } catch (err) {
      console.error('error in useInvoice reissueWithCancellation:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  const sendInvoiceMail = useCallback(async invoiceId => {
    try {
      const responseData = await sendRequest({
        url: `/api/v1/invoice/${invoiceId}/send-mail`,
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!responseData || responseData.success !== true) {
        throw new Error(responseData?.message || 'Mailversand fehlgeschlagen');
      }
      return responseData;
    } catch (err) {
      console.error('error in useInvoice sendInvoiceMail:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  const createExternalInvoice = useCallback(async payload => {
    try {
      const responseData = await sendRequest({
        url: '/api/v1/invoice/create-external',
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: payload,
      });
      assertInvoiceResponseOrThrow(responseData);
      return responseData;
    } catch (err) {
      console.error('error in useInvoice createExternalInvoice:', err);
      throw err;
    }
  }, [sendRequest, getAuthHeaders]);

  return {
    isLoading,
    error,
    getBookingInvoices,
    getInvoicePDF,
    listInvoices,
    markInvoicePaid,
    confirmNoShowPayment,
    cancelNoShowProcess,
    updateCollectionStatus,
    downloadKrefoZip,
    updateInvoiceCustomer,
    getInvoicePDFById,
    createCancellation,
    sendInvoiceMail,
    reissueWithCancellation,
    createExternalInvoice,
  };
};

export default useInvoice;
